/**
 * Live updates over DashboardHub's WebSocket (private/auth/src/
 * dashboardHub.ts) instead of a manual refresh click or a polling timer —
 * direct operator feedback, 2026-08-19: "Can it be a websocket that just
 * updates live?" / "I meant a websocket not polling." The socket carries
 * only a {type:"changed"} invalidation signal, not the actual table data —
 * subscribers re-fetch via the same REST endpoints their own load buttons
 * already use, so there's exactly one code path that turns broker state
 * into rendered rows, WS-triggered or click-triggered.
 *
 * Connects DIRECTLY to auth.pure.dev, bypassing this Worker's own /api/*
 * proxy entirely — the proxy only handles request/response HTTP, it has no
 * WebSocket-forwarding path, and a browser's cross-origin WebSocket has no
 * CORS restriction to work around in the first place (unlike fetch). Auth
 * via a ?token= query param, not an Authorization header — the browser
 * WebSocket constructor cannot set custom headers on the upgrade request.
 *
 * One singleton connection for the whole app (not one per page/component)
 * — every page that cares subscribes via onDashboardChanged, and the
 * connection itself is opened lazily on first subscriber and stays open
 * across client-side route changes.
 */
const AUTH_BROKER_WS_ORIGIN = "wss://auth.pure.dev";

type Listener = () => void;
const listeners = new Set<Listener>();

let ws: WebSocket | null = null;
let reconnectDelayMs = 1000;
let currentToken: string | null = null;

function connect(token: string): void {
  if (ws) return;
  ws = new WebSocket(
    AUTH_BROKER_WS_ORIGIN +
      "/v1/dashboard-ws?token=" +
      encodeURIComponent(token),
  );
  ws.onopen = () => {
    reconnectDelayMs = 1000; // reset backoff on a clean connect
  };
  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data as string) as { type?: string };
      if (msg.type === "changed") for (const l of listeners) l();
    } catch {
      /* ignore malformed frames */
    }
  };
  ws.onclose = ws.onerror = () => {
    ws = null;
    if (!currentToken) return; // torn down deliberately — don't reconnect
    setTimeout(() => connect(currentToken!), reconnectDelayMs);
    reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
  };
}

/** Subscribe to "something changed" — call your own refetch in `cb`. */
export function onDashboardChanged(token: string, cb: Listener): () => void {
  currentToken = token;
  connect(token);
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
